/** Ordinary rail controls while authority/rules are loading. Labels stay
 * readable; no price, size, duration or permission is inferred. This leaf has
 * no imports or event handlers, so it cannot gain trading authority. */
export function UsTicketLoading({ railControls, quickSizes = [], loading = true }: {
  railControls?: { sideCaption?: { text: string; title?: string }; priceLabelSuffix?: string };
  quickSizes?: readonly number[];
  loading?: boolean;
}) {
  const choice = 'min-w-0 flex-1 rounded-md bg-muted text-muted-foreground font-semibold disabled:cursor-not-allowed disabled:opacity-50';
  const priceLabel = `Price (¢)${railControls?.priceLabelSuffix ? ` · ${railControls.priceLabelSuffix}` : ''}`;
  return <div aria-label={loading ? 'Loading order controls' : 'Order controls unavailable'} aria-busy={loading} data-testid="us-ticket-loading">
    <fieldset disabled className="min-w-0 grid gap-3">
      <div className="space-y-1.5">
        <div role="group" aria-label="Buy side" className="flex gap-1.5">
          <button type="button" className={`${choice} px-2 py-1.5 text-xs`}>BUY YES</button>
          <button type="button" className={`${choice} px-2 py-1.5 text-xs`}>BUY NO</button>
        </div>
        <p className="text-muted-foreground min-h-4 break-words text-center text-[11px] [overflow-wrap:anywhere]"
          title={railControls?.sideCaption?.title}>{railControls?.sideCaption?.text ?? '\u00a0'}</p>
      </div>
      <div className="flex gap-1.5">
        <button type="button" className={`${choice} px-2 py-1 text-[11px]`}>LIMIT</button>
        <button type="button" className={`${choice} px-2 py-1 text-[11px]`}>MARKET</button>
      </div>
      <div role="group" aria-label="Order duration" className="flex gap-1.5">
        {['GOOD TO START', 'GOOD TO CLOSE', 'CUSTOM'].map(label => <button key={label} type="button"
          className={`${choice} px-1 py-1 text-[11px]`}>{label}</button>)}
      </div>
      <div className="grid min-w-0 grid-cols-2 gap-2">
        <div className="min-w-0">
          <span className="text-muted-foreground mb-1 block text-[10px]">{priceLabel}</span>
          <span className="border-border bg-input flex rounded-md border">
            <button type="button" aria-label="One tick lower" className="text-muted-foreground flex w-7 shrink-0 items-center justify-center opacity-30"><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /></svg></button>
            <input aria-label={priceLabel} type="text" value="" readOnly className="bg-transparent min-w-0 flex-1 py-1.5 text-center font-mono text-xs" />
            <button type="button" aria-label="One tick higher" className="text-muted-foreground flex w-7 shrink-0 items-center justify-center opacity-30"><svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5v14" /></svg></button>
          </span>
        </div>
        <label className="min-w-0">
          <span className="text-muted-foreground mb-1 block text-[10px]">Qty</span>
          <input type="text" value="" readOnly className="border-border bg-input text-foreground w-full rounded-md border px-2.5 py-1.5 font-mono text-xs tabular-nums" />
        </label>
        {quickSizes.length > 0 && <div className="col-span-2 -mt-1 flex items-center gap-1.5">
          <span className="text-muted-foreground mr-auto text-[10px]">Quick</span>
          {[...new Set(quickSizes)].map(size => <button key={size} type="button" className="bg-muted text-muted-foreground rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-none opacity-50">{size}</button>)}
        </div>}
      </div>
      <span className="text-[10px] text-muted-foreground">More order options</span>
    </fieldset>
    <dl className="border-border mt-3 space-y-1 rounded-lg border px-3 py-2 text-sm">
      {['Price', 'Total Cost (fee)', 'Max Payout', 'Max Profit'].map(label => <div key={label} className="flex items-center justify-between gap-x-2">
        <dt className="text-muted-foreground">{label}</dt><dd className="text-muted-foreground font-mono">—</dd>
      </div>)}
    </dl>
    <button type="button" disabled className="bg-primary text-primary-foreground mt-3 w-full cursor-not-allowed rounded-md py-2 text-xs font-semibold opacity-50">Place Order</button>
  </div>;
}
